import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Every action has a visible edge and a physical press state. The edge is a
  // translucent black/white tint so it reads on any fill a call site passes
  // (Tailwind 3 cannot emit an alpha on `currentColor`, so `border-current/N`
  // silently produces no CSS).
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-black/10 text-sm font-semibold shadow-sm ring-offset-background transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-house-out hover:-translate-y-px hover:shadow-md active:translate-y-0 active:scale-[0.97] motion-reduce:transform-none motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:translate-y-0 disabled:opacity-50 disabled:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 dark:border-white/15",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_2px_0_hsl(var(--foreground)/0.22)] hover:brightness-[0.98]",
        destructive:
          "bg-red-600 text-white shadow-[0_2px_0_rgb(153_27_27/0.45)] hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500",
        outline:
          "bg-card text-foreground hover:bg-muted hover:text-foreground dark:bg-slate-900 dark:hover:bg-slate-800",
        secondary:
          "bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700",
        ghost:
          "bg-muted/70 text-foreground hover:bg-accent hover:text-accent-foreground",
        link:
          "bg-primary/10 text-primary-ink underline-offset-4 shadow-none hover:bg-primary/20 hover:underline dark:text-cyan-300",
        navigation:
          "bg-sky-50 text-sky-800 hover:bg-sky-100 dark:bg-sky-950/60 dark:text-sky-200 dark:hover:bg-sky-900/70",
        back:
          "rounded-full bg-card text-foreground hover:bg-primary/10 hover:text-primary-ink dark:bg-slate-900 dark:hover:bg-cyan-950/50 dark:hover:text-cyan-200",
        // emerald-700, not 600: white on emerald-600 is 3.77:1 (fails AA), on 700 it is 5.48:1.
        success:
          "bg-emerald-700 text-white shadow-[0_2px_0_rgb(6_95_70/0.45)] hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-600",
        warning:
          "bg-amber-400 text-amber-950 shadow-[0_2px_0_rgb(180_83_9/0.38)] hover:bg-amber-500",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-lg px-8",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
